import { Controller } from '@hotwired/stimulus'
import { FetchRequest } from '@rails/request.js'
import { disableForm, enableForm } from '../src/helpers'
import { loadStripe } from '@stripe/stripe-js'

/**
 * Stripe Payment Element controller
 *
 * Handles two payment flows:
 * 1. One-time payments (forms): Uses PaymentIntent, confirms payment client-side
 * 2. Subscriptions/saved cards: Creates PaymentMethod, server handles charging
 *
 * The flow is determined by the presence of paymentIntentValue:
 * - With paymentIntent: Confirm payment immediately (one-time charges)
 * - Without paymentIntent: Create PaymentMethod and submit to server (subscriptions)
 */
export default class extends Controller {
  static targets = ['card', 'address', 'error', 'form']
  static values = {
    publicKey: String,
    paymentIntent: String,
    setupIntentUrl: String,
    useDefaultCard: { type: Boolean, default: false }
  }

  async connect() {
    this.stripe = await loadStripe(this.publicKeyValue)
    this.stripeElements = this.initializeElements()
    this.mountPaymentElement()
    if (this.hasAddressTarget) this.mountAddressElement()
  }

  // Toggle methods for switching between default and new card
  enableDefaultCard() {
    this.useDefaultCardValue = true
  }

  disableDefaultCard() {
    this.useDefaultCardValue = false
  }

  initializeElements() {
    const appearance = this.buildAppearance()

    if (this.hasPaymentIntentValue) {
      // One-time payments: use the PaymentIntent's client secret
      return this.stripe.elements({
        clientSecret: this.paymentIntentValue,
        appearance
      })
    } else if (this.hasSetupIntentUrlValue) {
      // Saving a card up front (membership applications): deferred mode.
      return this.stripe.elements({
        mode: 'setup',
        currency: 'usd',
        appearance
      })
    } else {
      // Subscriptions: deferred mode, create PaymentMethod on submit
      // SCA happens later on Pay's confirmation page
      return this.stripe.elements({
        mode: 'setup',
        currency: 'usd',
        paymentMethodCreation: 'manual',
        appearance
      })
    }
  }

  mountPaymentElement() {
    this.paymentElement = this.stripeElements.create('payment', {
      layout: 'tabs',
      terms: { card: 'never' }
    })
    this.paymentElement.mount(this.cardTarget)
  }

  mountAddressElement() {
    this.addressElement = this.stripeElements.create('address', {
      mode: 'billing'
    })
    this.addressElement.mount(this.addressTarget)
  }

  buildAppearance() {
    const getCSSVariable = (name) => {
      return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
    }

    const fontUrl = window.location.origin + RAILS_ASSET_URL('UniversNextPro430BasicRegular.woff2')

    return {
      theme: 'flat',
      variables: {
        fontFamily: 'UniversNextPro, sans-serif',
        fontSizeBase: getCSSVariable('--text-base'),
        fontLineHeight: getCSSVariable('--text-base--line-height'),
        colorPrimary: '#000000',
        colorText: '#000000',
        colorTextSecondary: getCSSVariable('--color-theme-gray'),
        colorBackground: getCSSVariable('--color-theme-light-gray'),
        borderRadius: '0px'
      },
      rules: {
        '.Input': {
          border: '2px solid #000000',
          padding: '0.6875rem'
        },
        '.Label': {
          fontSize: getCSSVariable('--text-base'),
          marginBottom: '0.25rem'
        }
      },
      fonts: [
        {
          family: 'UniversNextPro',
          src: `url(${fontUrl}) format("woff2")`,
          style: 'normal',
          weight: '400'
        }
      ]
    }
  }

  /**
   * Main form submission handler
   * Intercepts form submit to process payment through Stripe first
   */
  async submit(event) {
    // Let submitters that opt out (e.g. "Save progress") bypass Stripe and submit
    // normally, so the draft persists and no payment is attempted.
    // See https://github.com/new-art-dealers-alliance/nada/issues/1246
    if (event.detail.formSubmission?.submitter?.dataset.skipStripe) {
      return
    }

    // Stop form submission - we'll submit after Stripe processing
    // Either stop the Turbo formSubmission or prevent default
    if (event.detail.formSubmission) { 
      event.detail.formSubmission.stop()
    } else {
      event.preventDefault()
    }

    // Disable the form to prevent multiple submissions
    setTimeout(() => disableForm(this.formTarget), 50)

    if (this.useDefaultCardValue) {
      this.completeAndSubmit()
    } else if (this.hasPaymentIntentValue) {
      await this.confirmPayment()
    } else if (this.hasSetupIntentUrlValue) {
      await this.confirmSetup()
    } else {
      await this.createPaymentMethod()
    }
  }

  // ---------------------------------------------------------------------------
  // Payment flows
  // ---------------------------------------------------------------------------

  /**
   * For one-time payments (e.g., form fees)
   * Confirms the PaymentIntent client-side, then submits form
   */
  async confirmPayment() {
    const { error, paymentIntent } = await this.stripe.confirmPayment({
      elements: this.stripeElements,
      confirmParams: {
        return_url: window.location.href
      },
      redirect: 'if_required'
    })

    if (error) {
      this.handleError(error)
    } else if (paymentIntent?.status === 'succeeded') {
      this.completeAndSubmit()
    } else if (paymentIntent?.status === 'requires_action') {
      this.showError('Payment requires additional verification.')
    }
  }

  /**
   * For saving a card up front (e.g., membership applications)
   * Creates the SetupIntent server-side on submit (deferred), then confirms it client-side so
   * any 3-D Secure happens now, and submits the resulting PaymentMethod so the server can charge
   * it later off-session
   */
  async confirmSetup() {
    // Validate the card details before asking the server for a SetupIntent
    const { error: validationError } = await this.stripeElements.submit()

    if (validationError) {
      this.handleError(validationError)
      return
    }

    const clientSecret = await this.fetchSetupIntentClientSecret()
    if (!clientSecret) return

    const { error, setupIntent } = await this.stripe.confirmSetup({
      elements: this.stripeElements,
      clientSecret,
      confirmParams: {
        return_url: window.location.href
      },
      redirect: 'if_required'
    })

    if (error) {
      this.handleError(error)
    } else if (setupIntent?.status === 'succeeded') {
      this.addHiddenField('payment_method_token', setupIntent.payment_method)
      this.completeAndSubmit()
    } else if (setupIntent?.status === 'requires_action') {
      this.showError('Card verification requires additional steps.')
    }
  }

  async fetchSetupIntentClientSecret() {
    try {
      // FetchRequest handles the CSRF token and JSON Accept header for us
      const request = new FetchRequest('post', this.setupIntentUrlValue, { responseKind: 'json' })
      const response = await request.perform()
      if (!response.ok) throw new Error(`Request failed with status ${response.statusCode}`)

      const { client_secret: clientSecret } = await response.json
      return clientSecret
    } catch (error) {
      this.handleError({ message: 'Could not start card verification. Please try again.' })
      return null
    }
  }

  /**
   * For subscriptions and saving payment methods
   * Creates a PaymentMethod without triggering SCA, then submits to server
   * Server will create the subscription and handle SCA via Pay's confirmation page
   */
  async createPaymentMethod() {
    // Validate the payment details first
    const { error: validationError } = await this.stripeElements.submit()
    if (validationError) {
      this.handleError(validationError)
      return
    }

    // Create the PaymentMethod (no SCA here)
    const { error, paymentMethod } = await this.stripe.createPaymentMethod({
      elements: this.stripeElements
    })

    if (error) {
      this.handleError(error)
    } else {
      this.addHiddenField('processor', 'stripe')
      this.addHiddenField('payment_method_token', paymentMethod.id)
      this.completeAndSubmit()
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  addHiddenField(name, value) {
    const input = document.createElement('input')
    input.type = 'hidden'
    input.name = name
    input.value = value
    this.formTarget.appendChild(input)
  }

  handleError(error) {
    console.error('Stripe error:', error)
    this.dispatch('error', { detail: { error: error.message } })
    this.showError(error.message || 'Payment failed. Please try again.')
  }

  showError(message) {
    this.errorTarget.textContent = message
    this.errorTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setTimeout(() => enableForm(this.formTarget), 100)
  }

  completeAndSubmit() {
    const event = this.dispatch('complete')
    if (event.defaultPrevented) return
    this.formTarget.submit()
  }
}
